import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Smartphone, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GridBackground } from '@/components/GridBackground';
import { MainNavigation } from '@/components/MainNavigation';
import { QuickAnswerSection } from '@/components/QuickAnswerSection';
import { GateFAQSection } from '@/components/GateFAQSection';
import { Footer } from '@/components/Footer';
import { deviceGuidePillarAIO } from '@/data/deviceGuidePillarAIO';
import { loadAllArticles } from '@/services/articleLoader';
import { CTA1 } from '@/components/CTABanners';

const DEVICES_PILLAR_SLUG = 'devices-guide-2026';

export const DeviceGuidePillarPage = () => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const articles = await loadAllArticles();
        const found = articles.find(a => a.slug === DEVICES_PILLAR_SLUG);
        if (mounted) {
          setArticle(found || null);
          setLoading(false);
        }
      } catch (e) {
        console.warn('Failed to load devices guide pillar from /articles:', e);
        if (mounted) {
          setArticle(null);
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const quickAnswer = article?.quickAnswer || deviceGuidePillarAIO.quickAnswer;
  const faqItems = article?.faqItems && article.faqItems.length ? article.faqItems : deviceGuidePillarAIO.faqItems;

  if (loading && !article) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        Loading guide...
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article?.title || 'IPTV Devices Compatibility Guide 2026 | XtreamGate'}</title>
        <meta
          name="description"
          content={
            article?.excerpt ||
            'See which devices work best with XtreamCube IPTV in 2026 – from Firestick and smart TVs to phones, tablets and PCs.'
          }
        />
        <link rel="canonical" href="https://xtreamgate.com/guides/devices" />
      </Helmet>

      <div className="min-h-screen relative">
        <GridBackground />
        <MainNavigation />

        <main className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <Button asChild variant="outline" className="mb-8 glass border-primary/30">
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>

            {/* Page Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <Smartphone className="w-10 h-10 text-primary" />
                <h1 className="text-4xl sm:text-5xl font-bold text-white">
                  {article?.title || 'Device Compatibility Guide'}
                </h1>
              </div>
              <p className="text-xl text-white/70 max-w-3xl mx-auto">
                {article?.excerpt || 'Stream XtreamCube IPTV on 50+ devices with easy setup'}
              </p>
            </motion.div>

            {/* Quick Answer */}
            <QuickAnswerSection answer={quickAnswer} />

            {/* Supported Devices Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-12"
            >
              <h2 className="text-3xl font-bold text-white mb-8 text-center">
                Supported Devices
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {deviceGuidePillarAIO.supportedDevices.map((device, index) => (
                  <motion.div
                    key={device.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                    className="glass rounded-xl p-6 hover:bg-white/10 transition"
                    style={{ boxShadow: 'var(--shadow-glass)' }}
                  >
                    <div className="text-4xl mb-4">{device.icon}</div>
                    <h3 className="text-xl font-bold text-white mb-2">{device.name}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-white/60">Rating:</span>
                        <span className="text-primary">{device.rating}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-white/60">Setup:</span>
                        <span className="text-white/90">{device.setupTime}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-white/60">Status:</span>
                        <span className={device.compatibility === 'Excellent' ? 'text-green-400' : 'text-yellow-400'}>
                          {device.compatibility}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-xs text-white/70 mb-3">Recommended Apps:</p>
                      <div className="flex flex-wrap gap-2">
                        {device.apps.map(app => (
                          <span key={app} className="text-xs px-2 py-1 bg-primary/20 text-primary rounded">
                            {app}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-white/5 rounded-lg">
                      <p className="text-xs text-white/80">
                        <CheckCircle2 className="w-3 h-3 inline mr-1 text-primary" />
                        {device.bestFor}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* FAQ Section */}
            <div className="mt-16">
              <GateFAQSection faqItems={faqItems} />
            </div>

            {/* CTA Section */}
            <div className="mt-16">
              <CTA1
                headline="Ready to Get Started?"
                subtext="Try XtreamCube IPTV free for 24 hours on your device. No credit card required."
                buttonText="Start Free Trial"
                buttonUrl="https://xtreamcube.shop/freetrialiptv/"
                showIcon={false}
              />
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};
